import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { M2Layout, PageHero, SectionIndex, FigCap, Arrow, CTABand } from "../../components/machines2/Blocks";
import { HairlineList } from "../../components/enterprise2/HairlineList";
import { Accordion } from "../../components/enterprise2/Accordion";
import { PullQuote } from "../../components/enterprise2/EditorialPolish";
import { Reveal } from "../../components/enterprise2/Reveal";
import { SubNav } from "../../components/enterprise2/SubNav";
import { INDUSTRIES, INDUSTRY_LIST } from "../../data/machines2";
import { SECTOR_STORIES } from "../../data/machines2Stories";

export function Machines2Industries() {
  return (
    <M2Layout active="industries" title="Industries | Crowther for Machines">
      <PageHero
        eyebrow="Industries"
        title="Machine intelligence, where the work actually happens."
        sub="Six sectors where physical AI compounds value hour after hour, shift after shift, season after season."
        testId="m2-industries-hero"
      />
      <Reveal as="section" className="e2-shell e2-section" data-testid="m2-industries-grid">
        <SectionIndex n="01" label="Six sectors" />
        <FigCap n="1" label="Where the machines work" style={{ marginTop: 8, marginBottom: 24 }} />
        <div className="e2-thinking-grid">
          {INDUSTRY_LIST.map((ind, i) => (
            <Link key={ind.slug} to={`/machines-2/industries/${ind.slug}`} className="e2-thinking-card" data-testid={`m2-industries-card-${ind.slug}`}>
              <div className={`e2-thinking-cover ${["forest", "navy", "teal"][i % 3]}`}>
                <span className="e2-thinking-cover-glow" aria-hidden />
                <span className="e2-thinking-cover-text">{ind.title}</span>
              </div>
              <div className="e2-thinking-body">
                <p className="e2-thinking-title">{ind.line}</p>
                <span className="e2-thinking-read">Read the sector view <Arrow /></span>
              </div>
            </Link>
          ))}
        </div>
      </Reveal>
      <CTABand line="Find out more about Crowther for machines." testId="m2-industries-cta" />
    </M2Layout>
  );
}

const DETAIL_SUBNAV = [
  { id: "problem", label: "The problem" },
  { id: "deploy", label: "What we deploy" },
  { id: "change", label: "What changes" },
  { id: "products", label: "Products" },
  { id: "advantages", label: "Advantages" },
  { id: "questions", label: "Questions" },
];

export function Machines2Industry() {
  const { slug } = useParams();
  const ind = INDUSTRIES[slug];
  const story = SECTOR_STORIES[slug];
  if (!ind || !story) return <Navigate to="/machines-2/industries" replace />;

  const Narrative = ({ id, n, label, title, paras, aside }) => (
    <Reveal as="section" id={id} className="e2-shell e2-section" data-testid={`m2-industry-${id}`}>
      <SectionIndex n={n} label={label} />
      <div className="e2-split">
        <div className="e2-split-title-col">
          <h2 className="e2-h-l">{title}</h2>
        </div>
        <div>
          {paras.map((t, i) => <p key={i} className={i === 0 ? "e2-body e2-body-lg" : "e2-body"} style={i ? { marginTop: 18 } : undefined}>{t}</p>)}
          {aside}
        </div>
      </div>
    </Reveal>
  );

  return (
    <M2Layout active="industries" title={`${ind.title} | Crowther for Machines`}>
      <PageHero eyebrow={ind.title} title={ind.heroHeadline} sub={ind.heroSubline} testId="m2-industry-hero">
        <Link to="/machines-2/industries" className="e2-btn-link" data-testid="m2-industry-back">All sectors <Arrow /></Link>
      </PageHero>
      <SubNav items={DETAIL_SUBNAV} testId="m2-industry-subnav" />

      <Reveal as="section" className="e2-shell e2-section" data-testid="m2-industry-positioning">
        <PullQuote mark="&ldquo;">{ind.positioning}</PullQuote>
      </Reveal>

      <Narrative id="problem" n="01" label="The problem" title={story.problemTitle} paras={story.problem} />
      <Narrative
        id="deploy" n="02" label="What Crowther deploys" title={story.deployTitle} paras={story.deploy}
        aside={
          <div style={{ marginTop: 32 }}>
            <FigCap n="1" label={`The stack, deployed for ${ind.title.toLowerCase()}`} />
            <div className="m2-figure navy" data-testid="m2-industry-figure">
              <div className="m2-figure-head"><span>FIG.1 &nbsp;·&nbsp; {ind.title.toUpperCase()}</span><span>ON SITE</span></div>
              <div className="m2-stack-figure-layers">
                {story.figure.layers.map((l) => <div key={l} className="m2-stack-layer"><span className="m2-stack-name">{l}</span></div>)}
                <div className="m2-stack-layer base"><span className="m2-stack-name">{story.figure.base}</span></div>
              </div>
              <div className="m2-figure-foot"><span>■ SOVEREIGN MACHINE STACK</span><span>JQ &gt; 1.0</span></div>
            </div>
          </div>
        }
      />
      <Narrative
        id="change" n="03" label="What changes" title={story.changeTitle} paras={story.change}
        aside={<p className="e2-meta" style={{ marginTop: 22 }}>Written from our work with {story.descriptor} and comparable operations. We do not name clients.</p>}
      />

      <Reveal as="section" id="products" className="e2-shell e2-section" data-testid="m2-industry-products">
        <SectionIndex n="04" label="Products for this industry" />
        <HairlineList rows={ind.products.map((p) => ({ testId: `m2-industry-product-${p.name.toLowerCase().replace(/\s+/g, "-")}`, name: p.name, desc: p.body }))} />
      </Reveal>

      <Reveal as="section" id="advantages" className="e2-shell e2-section" data-testid="m2-industry-advantages">
        <SectionIndex n="05" label="Advantages" />
        <div className="e2-arg-facts" style={{ marginTop: 24 }}>
          {ind.advantages.map((a, i) => (
            <div key={a.name} className="e2-arg-fact" data-testid={`m2-industry-advantage-${i + 1}`}>
              <div className="e2-arg-fact-n">0{i + 1}</div>
              <div><div className="e2-arg-fact-b" style={{ fontSize: 22, marginBottom: 8 }}>{a.name}</div><p className="e2-body" style={{ margin: 0 }}>{a.body}</p></div>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal as="section" id="questions" className="e2-shell e2-section" data-testid="m2-industry-faq">
        <SectionIndex n="06" label="Questions owners ask" />
        <div className="e2-split" style={{ marginTop: 24 }}>
          <div className="e2-split-title-col">
            <h2 className="e2-h-l">The questions we hear first.</h2>
          </div>
          <Accordion items={ind.faq} />
        </div>
      </Reveal>

      <CTABand line={ind.ctaLine} testId="m2-industry-cta" />
    </M2Layout>
  );
}
